import React, { useState, MouseEvent, TouchEvent, useEffect } from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import Fonts from "../../../ui/global/Fonts";
import Icon from "../../../ui/global/Icon";
import Button from "../../../ui/components/Button";
import MenuOpen from "../../../ui/components/MenuOpen";
import Toggle from "../../../ui/components/Toggle";
import ToolbarHistory from "./ToolbarHistory";
import { HistoryEntry as HistoryEntryProps } from "../utils/useArtHistory";
import Tooltip from "../../../ui/components/Tooltip";
import HistoryPanel from "./HistoryPanel";
import InputSimple from "../../../ui/components/InputSimple";
import ProfileButton from "../../website/ProfileButton";
import GameArtMenu from "./GameArtMenu";
import { ArtType } from "../Art";
import { Phases3D } from "../utils/useAI";
import { useCreditsContext } from "../../pricing/CreditsContext";
import HomeLogoButton from "../../website/HomeLogoButton";
import { CREDITS_CONSTANTS } from "../../../backend/useCredits";

const TopContainer = styled.div`
    position: relative;
    top: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0;
    margin: 0;
`;

const Toolbar = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: fit-content;
    margin: 0;
    padding: 0 5px 5px 5px;
    gap: 10px;
    z-index: 100;
    overflow: visible;
`;

const Left = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
    margin: 0;
`;

const Middle = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex: 1;
    gap: 6px;
    margin: 0;
`;

const Right = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    margin: 0;
`;

const EnergyContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    height: fit-content;
    border-radius: 20px;
    border: 1px solid ${Colors.grey2};
    padding: 6px 10px 6px 8px;
    margin-top: auto;
    margin-bottom: auto;
    margin-right: 10px;
    gap: 5px;
`;

const EnergyText = styled.div`
    font-size: 16px;
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-family: ${Fonts.lexendMedium.fontFamily};
    color: ${Colors.black};
    line-height: 1.2em;
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${Colors.grey1};
    width: 1px;
    height: 24px;
    margin: auto;
`;

const ArtName = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 16px;
    line-height: 1.2em;
    background-color: ${Colors.transparent};
    color: ${Colors.black};
    border: 1px solid ${Colors.transparent};
    text-wrap: nowrap;
    border-radius: 8px;
    // margin: 10px;

    &:hover {
        background-color: ${Colors.white};
        border: 1px solid ${Colors.grey1};
    }

    @media (max-width: 480px) {
        display: none;
    }
`;

interface ArtTopBarProps {
    config: any;
    artType: ArtType;
    artId: string;
    userPng: string;
    aiImage: string;
    aiToggleInitial: boolean;
    onAIToggleChange: (enabled: boolean) => void;
    history: HistoryEntryProps[];
    currentIndex: number;
    onSelectState: ( state: HistoryEntryProps ) => void;
    onStartOver: () => void;
    onRemoveBGClick: () => void;
    artName: string;
    handleArtNameChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    handleHomeClick: () => void;
    onGenerateClick: () => void;
    isGeneratingImage: boolean;
    onNewProjectClick: () => void;
    hasDrawing: boolean;
    model3D: any;
    on3DClick?: () => void;
    isGenerating3D?: boolean;
    button3dPhase: Phases3D;
    description: string;
}

const ArtTopBar = ({ config, artType, artId, userPng, aiImage, aiToggleInitial, onAIToggleChange, history, currentIndex, onSelectState, onStartOver, onRemoveBGClick, artName, handleArtNameChange, handleHomeClick, onGenerateClick, isGeneratingImage, onNewProjectClick, hasDrawing, model3D, on3DClick, isGenerating3D, button3dPhase, description }: ArtTopBarProps) => {
    const { isOutOfCredits } = useCreditsContext();
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [aiToggle, setAiToggle] = useState(aiToggleInitial);

    const handleClick = (event?: MouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>) => {
        // console.log('Opening download menu');
        if (event) {
            if (menuAnchorEl) {
                setMenuAnchorEl(null);
            } else {
                setMenuAnchorEl(event.currentTarget);
            }
        }
    };

    const handleClose = () => {
        // console.log('Closing download menu');
        setMenuAnchorEl(null);
    };

    const handleDownloadImage = async () => {
        // console.log('Downloading image: ', aiImage);
        try {
            const response = await fetch(aiImage);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'image.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            handleClose();
        } catch (error) {
            console.error('Error downloading image:', error);
        }
    };

    const handleCopyImage = async () => {
        // console.log('Copying image: ', aiImage);
        try {
            // Fetch the image data
            const response = await fetch(aiImage);
            const blob = await response.blob();
            // Copy to clipboard
            await navigator.clipboard.write([
                new ClipboardItem({
                    [blob.type]: blob
                })
            ]);
            // console.log('Image copied to clipboard successfully');
        } catch (error) {
            console.error('Failed to copy image: ', error);
        }
        handleClose();
    };

    const handleDownload3D = async () => {
        // console.log('Downloading 3D: ', model3D?.url);
        try {
            const response = await fetch(model3D?.url || '');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'model.glb';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            handleClose();
        } catch (error) {
            console.error('Error downloading 3D model:', error);
        }
    };

    const menuOptions2d = [
        // { text: "New Project", icon: "plus", onClick: onNewProjectClick }, // NOT IMPLEMENTED
        { text: "Delete All", icon: "trash-outline", onClick: onStartOver, disabled: history.length === 0 || (history[currentIndex].aiImage === '' && history[currentIndex].userPng === '') },
        { text: "Download image", icon: "download", onClick: handleDownloadImage, disabled: aiImage === '' },
        { text: "Copy image", icon: "copy", onClick: handleCopyImage, disabled: aiImage === '' },
    ];

    const menuOptions3d = [
        { text: "Delete All", icon: "trash-outline", onClick: onStartOver, disabled: history.length === 0 || (history[currentIndex].aiImage === '' && history[currentIndex].userPng === '') },
        { text: "Download Image", icon: "download", onClick: handleDownloadImage, disabled: aiImage === '' },
        { text: "Download 3D", icon: "download", onClick: handleDownload3D, disabled: model3D?.url === '' },
    ];

    const menuOptions = artType === '3D' ? menuOptions3d : menuOptions2d;

    const onToggleAI = () => {
        const currentState = aiToggle;
        // console.log('Toggle AI to: ', !currentState);
        setAiToggle(!currentState);
        onAIToggleChange(!currentState);
    };

    useEffect(() => {
        onAIToggleChange(aiToggle);
    }, []);

    const onAnimateClick = () => {
        // console.log('Animate clicked');
        // TODO: Implement animate
    };

    const render3dButtons = () => {
        return (
            <>
            {button3dPhase === 'image' && (
                <>
                {/* <Tooltip sub_text='1 credit' colorMode="dark" position="top"> */}
                    <Button
                        size="tiny"
                        color="black"
                        icon="image"
                        text="Create Image"
                        onClick={onGenerateClick}
                        disabled={(!hasDrawing && description === '') || isGeneratingImage || isOutOfCredits.art_2d_limited}
                    />
                {/* </Tooltip> */}
                <Tooltip text="Generate 3D" colorMode="dark" position="top"> {/*  sub_text={`${CREDITS_CONSTANTS.CREDIT_COSTS.COST_ART_3D} credits`} */}
                    <Button
                        size="tiny"
                        color="white"
                        icon="cube"
                        onClick={on3DClick}
                        disabled={!aiImage || isGenerating3D || isOutOfCredits.art_3d_limited}
                    />
                </Tooltip>
                </>
            )}

            {button3dPhase === '3d' && (
                <>
                <Tooltip text="Create Image"colorMode="dark" position="top">
                    <Button
                        size="tiny"
                        color="white"
                        icon="image"
                        onClick={onGenerateClick}
                        disabled={(!hasDrawing && description === '') || isGeneratingImage || isOutOfCredits.art_2d_limited}
                />
                </Tooltip>
                {/* <Tooltip sub_text={`${CREDITS_CONSTANTS.CREDIT_COSTS.COST_ART_3D} credits`} colorMode="dark" position="top"> */}
                    <Button
                        size="tiny"
                        color="black"
                        icon="cube"
                        text="Generate 3D"
                        onClick={on3DClick}
                        disabled={!aiImage || isGenerating3D || isOutOfCredits.art_3d_limited}
                    />
                {/* </Tooltip> */}
                </>
            )}
            </>
        )
    };

    const renderLeft = () => {
        return (
            <Left>
                <HomeLogoButton onHomeClick={handleHomeClick} linkTo="/art" colorMode="light" />
                {/* <Divider /> */}
                <ArtName>
                    <InputSimple
                        value={artName}
                        colorMode='light'
                        textAlign='left'
                        onChange={handleArtNameChange}
                        onEnterPress={() => {}}
                    />
                </ArtName>
            </Left>
        )
    };

    const renderMiddle = () => {
        return (
        <>
            {/* <Divider /> */}
            <Middle>
                {config.SHOW_NEW_HISTORY ? (
                    <Tooltip text="History" colorMode="dark">
                        <HistoryPanel 
                            history={history}
                            currentIndex={currentIndex}
                            config={config}
                            onSelectState={(entry: HistoryEntryProps) => onSelectState(entry)}
                        />
                    </Tooltip>
                ) : (
                    <ToolbarHistory
                        history={history}
                        currentIndex={currentIndex}
                        onSelectState={(entry: HistoryEntryProps) => onSelectState(entry)}
                        onStartOver={onStartOver}
                        config={config}
                    />
                )}
            </Middle>
            {/* <Divider /> */}
        </>
        )
    };

    const renderRight = () => {
        return (
            <Right>
                {artType === '2D' ? (
                    <>
                        {/* <Tooltip text="Real-Time Image Generation" colorMode="dark"> */}
                        {config.ART_REALTIME && (
                            <Toggle 
                                checked={aiToggle}
                                label={{ text: "AI", placement: "start" }}
                                onChange={onToggleAI}
                            />
                        )}
                        {/* </Tooltip> */}

                        <Tooltip text="Remove Background" colorMode="dark">
                            <Button
                                size="small"
                                color="ghost"
                                icon="remove-bg"
                                onClick={onRemoveBGClick}
                                disabled={aiImage === ''}
                            />
                        </Tooltip>

                        {config.ENABLE_ANIMATE_BUTTON_IN_ART && (
                            <Tooltip text="Animate" colorMode="dark">
                                <Button
                                    size="small"
                                    color="ghost"
                                    icon="animate"
                                    onClick={onAnimateClick}
                                    disabled={aiImage === ''}
                                />
                            </Tooltip>
                        )}
                    </>
                ) : (
                    <>
                        {render3dButtons()}
                    </>
                )}

                <GameArtMenu 
                    config={config}
                    aiImage={aiImage}
                    artId={artId}
                    artName={artName}
                    history={history}
                    currentIndex={currentIndex}
                />
                
                <Button 
                    size="small" 
                    color="ghost" 
                    icon="more" 
                    onClick={handleClick}
                />
                <MenuOpen
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleClose}
                    options={menuOptions}
                    minWidth={80}
                />

                <ProfileButton colorMode="light" config />
            </Right>
        )
    };

    return (
        <TopContainer>
            <Toolbar id='toolbar-top'>
                {renderLeft()}
                {window.innerWidth > 768 && renderMiddle()}
                {renderRight()}
            </Toolbar>
            {/* {window.innerWidth <= 768 && (
                <Toolbar id='toolbar-top-middle'>
                    {renderMiddle()}
                </Toolbar>
            )} */}
        </TopContainer>
    );
};

export default ArtTopBar;